import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { HeatmapSlot, Rack } from '../../models/types';
import { Avatar, AvatarImage, AvatarFallback } from '@shared/components/ui/shadcn/avatar';
import { useToast } from '@shared/hooks/use-toast';

interface RackLocationModalProps {
  rack: Rack | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editMode?: boolean;
  onUpdateRack?: (params: {
    rackId: number;
    payload: { shelves?: number; slots_per_shelf?: number; is_endcap?: boolean };
  }) => Promise<unknown>;
  heatmapShelves?: Array<{ shelf_index: number; slots: HeatmapSlot[] }> | null;
}

const ZONE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  cold: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'cold',
  },
  temperate: {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
    label: 'temperate',
  },
  hot: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-700 dark:text-red-300',
    label: 'hot',
  },
};

export const RackLocationModal = ({
  rack,
  open,
  onOpenChange,
  editMode,
  onUpdateRack,
  heatmapShelves,
}: RackLocationModalProps) => {
  const { t } = useTranslation('warehouseMapModule');
  const { toast } = useToast();
  const [shelves, setShelves] = useState<number>(1);
  const [slotsPerShelf, setSlotsPerShelf] = useState<number>(1);
  const [isEndcap, setIsEndcap] = useState<boolean>(false);

  useEffect(() => {
    if (rack) {
      setShelves(rack.shelves ?? 1);
      setSlotsPerShelf(rack.slots_per_shelf ?? 1);
      setIsEndcap(rack.is_endcap ?? false);
    }
  }, [rack]);

  if (!rack) return null;

  const locationsWithStock = (rack.locations || []).filter((loc) => loc.stock_location !== null);
  const totalProducts = locationsWithStock.length;
  const totalStock = locationsWithStock.reduce((sum, loc) => sum + (loc.stock_location?.stock ?? 0), 0);

  const handleSave = async () => {
    if (!onUpdateRack) return;
    try {
      await onUpdateRack({
        rackId: rack.id,
        payload: { shelves, slots_per_shelf: slotsPerShelf, is_endcap: isEndcap },
      });
      toast({ title: t('rackLocations.saveSuccess'), variant: 'default' });
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast({ title: t('rackLocations.saveError'), description: message, variant: 'destructive' });
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg max-h-[80vh] overflow-y-auto">
          <DialogPrimitive.Title className="text-base font-semibold mb-1">
            {t('rackLocations.title', { rack: rack.code })}
          </DialogPrimitive.Title>

          <div className="text-xs text-muted-foreground mb-4">
            {t('rackLocations.position', { row: rack.row, col: rack.col })} · {totalProducts}{' '}
            {t('rackLocations.productsLabel')} · {totalStock} unidades
          </div>

          <div className="flex gap-3 mb-4 p-3 rounded-md border bg-card/50">
            <label className="flex flex-col text-xs font-medium text-muted-foreground">
              {t('rackLocations.shelvesLabel')}
              <input
                type="number"
                min={1}
                value={shelves}
                readOnly={!editMode}
                onChange={(e) => setShelves(e.target.value === '' ? 1 : parseInt(e.target.value, 10))}
                className={`mt-1 w-20 rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
                  editMode ? 'cursor-pointer' : 'bg-muted/50 cursor-not-allowed'
                }`}
              />
            </label>
            <label className="flex flex-col text-xs font-medium text-muted-foreground">
              {t('rackLocations.slotsLabel')}
              <input
                type="number"
                min={1}
                value={slotsPerShelf}
                readOnly={!editMode}
                onChange={(e) => setSlotsPerShelf(e.target.value === '' ? 1 : parseInt(e.target.value, 10))}
                className={`mt-1 w-20 rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
                  editMode ? 'cursor-pointer' : 'bg-muted/50 cursor-not-allowed'
                }`}
              />
            </label>
            <div className="flex flex-col justify-end">
              <label className="text-xs font-medium text-muted-foreground">{t('rackLocations.isEndcapLabel')}</label>
              {editMode ? (
                <input
                  type="checkbox"
                  checked={isEndcap}
                  onChange={(e) => setIsEndcap(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300"
                />
              ) : (
                <div className="mt-1 text-xs text-muted-foreground">{isEndcap ? 'Sí' : 'No'}</div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {locationsWithStock.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">{t('rackLocations.empty')}</div>
            ) : (
              locationsWithStock.map((loc) => {
                const productName = loc.product_name || loc.code;
                const productImage = loc.stock_location?.product?.image_url;
                const initial = productName?.charAt(0)?.toUpperCase() || '?';

                return (
                  <div key={loc.id} className="flex items-center gap-3 p-2 rounded-md border bg-card/50">
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      {productImage ? <AvatarImage src={productImage} alt={productName} /> : null}
                      <AvatarFallback className="bg-gray-200 text-gray-700 text-xs font-medium">
                        {initial}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{productName}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('rackLocations.shelfSlot', {
                          shelf: loc.shelf ?? '-',
                          slot: loc.slot ?? '-',
                        })}{' '}
                        · {t('rackLocations.label', { label: loc.location_label || '-' })}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold">{loc.stock_location?.stock}</div>
                      <div className="text-xs text-muted-foreground">{t('rackLocations.units')}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Heatmap / Zone display */}
          {heatmapShelves && heatmapShelves.length > 0 && (
            <div className="mt-4 pt-3 border-t">
              <div className="text-xs font-medium text-muted-foreground mb-2">{t('rackLocations.heatmap.title')}</div>

              {/* Legend */}
              <div className="flex items-center gap-3 mb-2">
                {Object.values(ZONE_COLORS).map((zone) => (
                  <div key={zone.label} className="flex items-center gap-1">
                    <div className={`w-4 h-4 rounded ${zone.bg} ${zone.text}`} />
                    <span className="text-xs text-muted-foreground">
                      {t(`rackLocations.heatmap.zones.${zone.label}`)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-1">
                {heatmapShelves.map((shelf) => (
                  <div key={shelf.shelf_index} className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs text-muted-foreground min-w-[4ch]">
                      {t('rackLocations.heatmap.shelf', { shelf: shelf.shelf_index + 1 })}
                    </span>
                    <div className="flex gap-0.5">
                      {shelf.slots.map((slot) => {
                        const zone = ZONE_COLORS[slot.zone];
                        return (
                          <div
                            key={`${slot.shelf_index}:${slot.slot_index}`}
                            className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-medium ${zone.bg} ${zone.text}`}
                          >
                            {slot.slot_index + 1}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty locations */}
          {(rack.locations || []).length > 0 && locationsWithStock.length < (rack.locations || []).length && (
            <div className="mt-4 pt-3 border-t">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {t('rackLocations.emptyLocations', {
                  count: (rack.locations || []).length - locationsWithStock.length,
                })}
              </div>
            </div>
          )}

          {(rack.locations || []).length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center">{t('rackLocations.noLocations')}</div>
          )}

          {/* Save button (edit mode only) */}
          {editMode && (
            <div className="mt-4 pt-3 border-t flex justify-end">
              <button
                onClick={handleSave}
                disabled={!onUpdateRack}
                className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
              >
                {t('rackLocations.save')}
              </button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
